import { z } from "zod";
import { CatalogCardSchema } from "../catalog/catalog.js";
import { IsoDateTimeSchema } from "../common/primitives.js";
/** Where a listing was found. "ebay" = eBay's official API (checked data); "web" = web search (any site). */
export const SEARCH_SOURCES = ["ebay", "web"];
export const SearchSourceSchema = z.enum(SEARCH_SOURCES);
/**
 * How far to trust a price:
 * checked  — from an official API (eBay), current at the time shown;
 * hint     — read from a search snippet, may be out of date;
 * unknown  — no price available.
 */
export const PRICE_CONFIDENCE = ["checked", "hint", "unknown"];
export const PriceConfidenceSchema = z.enum(PRICE_CONFIDENCE);
export const MATCH_LEVELS = ["exact", "likely", "uncertain"];
export const MatchLevelSchema = z.enum(MATCH_LEVELS);
/** Money is a decimal string (never a float) plus an ISO 4217 currency code. */
export const MoneySchema = z.object({
    amount: z.string().regex(/^\d{1,12}(\.\d{1,4})?$/, "Invalid amount"),
    currency: z.string().regex(/^[A-Z]{3}$/, "Invalid currency"),
});
/**
 * Whether the item can still be bought:
 * available / limited / sold_out / ended — eBay said so (live);
 * may_be_sold_out — the web page text says "sold out" (may be out of date);
 * unknown — no information (most web results).
 * An error never produces sold_out or ended.
 */
export const AVAILABILITY_STATUSES = [
    "available",
    "limited",
    "sold_out",
    "ended",
    "may_be_sold_out",
    "unknown",
];
export const AvailabilityStatusSchema = z.enum(AVAILABILITY_STATUSES);
export const AvailabilitySchema = z.object({
    status: AvailabilityStatusSchema,
    /** How many are left, when eBay says (eBay rounds large numbers). */
    availableQuantity: z.number().int().min(0).nullable(),
    /** How many have sold from this listing, when eBay says. */
    soldQuantity: z.number().int().min(0).nullable(),
    /** When the listing ends (auctions) or ended. */
    endsAt: IsoDateTimeSchema.nullable(),
});
/** How worn the card looks, from the listing's own words ("DMG", "HP", "MP", "LP"). Worst first. */
export const WEAR_GRADES = ["damaged", "heavily_played", "moderately_played", "lightly_played"];
export const WearSchema = z.enum(WEAR_GRADES);
export const ListingResultSchema = z.object({
    /** Pokifolio's id for this listing (stable across searches). */
    id: z.uuid(),
    source: SearchSourceSchema,
    /** Website name for display, e.g. "eBay", "daraz.pk". */
    website: z.string().min(1),
    title: z.string().min(1),
    url: z.url(),
    imageUrl: z.url().nullable(),
    price: MoneySchema.nullable(),
    priceConfidence: PriceConfidenceSchema,
    /** The seller's own description of the condition, e.g. "Ungraded", "Near Mint". */
    condition: z.string().nullable(),
    listingType: z.enum(["fixed_price", "auction", "unknown"]),
    /** ISO country code of the item, when known. */
    itemCountry: z.string().nullable(),
    availability: AvailabilitySchema,
    /** null when nothing in the listing says the card is worn. */
    wear: WearSchema.nullable(),
    match: z.object({
        score: z.number().min(0).max(1),
        level: MatchLevelSchema,
        /** Plain-language reasons, e.g. "Card number XY28 found". */
        reasons: z.array(z.string()),
    }),
    foundAt: IsoDateTimeSchema,
});
export const SOURCE_STATUSES = ["ok", "failed", "not_configured"];
export const SourceStatusSchema = z.object({
    source: SearchSourceSchema,
    status: z.enum(SOURCE_STATUSES),
    resultCount: z.number().int().min(0),
    /** Shown to the user when the source failed or is not set up. */
    message: z.string().optional(),
});
/** POST /api/v1/searches — search for a chosen catalog card, or for free text. */
export const SearchRequestSchema = z
    .object({
    cardId: z.string().trim().min(1).max(64).optional(),
    query: z.string().trim().min(2, "Type at least 2 characters").max(120, "Search is too long").optional(),
})
    .refine((v) => v.cardId !== undefined || v.query !== undefined, {
    message: "Choose a card or type what to search for",
    path: ["query"],
});
export const SearchResponseSchema = z.object({
    searchId: z.uuid(),
    /** The text that was searched for. */
    query: z.string(),
    card: CatalogCardSchema.nullable(),
    sources: z.array(SourceStatusSchema),
    results: z.array(ListingResultSchema),
});
//# sourceMappingURL=search.js.map